import { Component, Input, OnInit, forwardRef, OnDestroy } from '@angular/core';
import { FormControl, ControlValueAccessor, NG_VALUE_ACCESSOR, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subject, Subscription, debounceTime, map, takeUntil } from 'rxjs';
import moment from 'moment';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'app-date',
    templateUrl: './date.component.html',
    imports: [
        CommonModule,
        ReactiveFormsModule
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DateComponent),
            multi: true,
        },
    ]
})
export class DateComponent implements ControlValueAccessor, OnInit, OnDestroy {
    @Input() required = false;
    @Input() name!: string;
    @Input() props!: any;
    @Input() id!: string;
    @Input() formControl!: FormControl;
    // @Input() form!: FormGroup;

    now = new Date();
    maxYear!: number;
    minYear!: number;
    errors = [];

    private destroy$ = new Subject<void>();
    dateChangeSubscription: Subscription | undefined;

    private onChange: (value: string) => void = () => { };
    private onTouched: () => void = () => { };

    date: any = {
        day: null,
        month: null,
        year: null
    };

    dateForm = new FormGroup({
        day: new FormControl(''),
        month: new FormControl(''),
        year: new FormControl(''),
    });

    ngOnInit() {
        this.maxYear = this.now.getFullYear() - parseInt(this.props.minAge);
        this.minYear = this.now.getFullYear() - parseInt(this.props.maxAge);

        const date = this.parseDate(this.formControl.value);

        if (date != null) {
            this.date.day = date.getDate();
            this.date.month = date.getMonth() + 1;
            this.date.year = date.getFullYear();

            this.dateForm.setValue(this.date);
        }

        this.dateChangeSubscription = this.dateForm.valueChanges.pipe(
            debounceTime(1000),
            map((date) => this.onInputChange(date)),
            takeUntil(this.destroy$))
            .subscribe();

    }

    // ngAfterViewChecked() {
    //     this.onInputChange(this.date);
    // }

    parseDate(myDate: never | object, myTimezone?: any) {
        let res: any = null;
        if (myDate !== undefined && myDate !== null) {
            if (myDate instanceof Date) {
                res = myDate;
            } else {
                if (typeof myDate == "number" || typeof myDate == "string") {
                    res = new Date(isNaN(myDate) ? myDate : parseInt(myDate, 10));
                    res = this.adjustTimezone(res, myTimezone);
                }
            }
        }

        return res;
    }

    adjustTimezone(myDate: Date, myTimezone: any) {
        const offset = isNaN(myTimezone) ? new Date().getTimezoneOffset() : parseFloat(myTimezone) * 60;
        return new Date(myDate.getTime() + offset * 60 * 1000);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    writeValue(_value: any): void {
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    onInputChange(date: any): void {
        this.date = date;

        // allowInvalid = true - set the value regardless, the validation will still reflect
        this.onChange(new Date(this.date.year, this.date.month - 1, this.date.day).toISOString());

        // run validations for the entire date state
        const errors: any = [];

        const yearIsValid = !!this.date.year && this.date.year <= this.maxYear;

        if (!yearIsValid) {
            errors.push('dateInvalid')
        }

        const monthIsValid = !!this.date.month;
        const dayIsValid = !!this.date.day;

        if (this.props.required === true) {
            const isValid = yearIsValid && monthIsValid && dayIsValid;

            if (!isValid) {
                errors.push('required')
            }
        }

        if (yearIsValid && monthIsValid && dayIsValid) {
            const dateIsValid = this.date.day <= this.maxDay(this.date.month, this.date.year);

            if (!dateIsValid) {
                errors.push('dateInvalid');
            }
            else {
                const meetsMinAge = this.meetsMinAge(this.date.day, this.date.month, this.date.year, this.props.minAge);
                const meetsMaxAge = this.meetsMaxAge(this.date.day, this.date.month, this.date.year, this.props.maxAge);

                if (!meetsMinAge) {
                    errors.push('meetsMinAge');
                }
                else if (!meetsMaxAge) {
                    errors.push('meetsMaxAge');
                }
            }
        }

        // change detection workaround
        this.formControl.setErrors(null);

        if (errors.length > 0) {
            this.formControl.setErrors(errors);
        }
        // else {
        // move the onChange function here to only set the model value if valid (allowInvalid = false)
        // note, the initial value, if it exists, will still be written regardless
        // }

        // allowInvalid = true
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    maxDay(month: number, year: number) {
        let res = 31;
        if (month != null) {
            if (month === 4 || month === 6 || month === 9 || month === 11) {
                res = 30;
            }
            if (year != null && month === 2) {
                res = year % 4 === 0 && year % 100 !== 0 ? 29 : 28;
            }
        }
        return res;
    }

    meetsMinAge(date: Date, month: number, year: number, minAge: number) {
        const today = new Date();
        const working = today.setFullYear(today.getFullYear() - minAge);
        const momentOfBirth = moment("" + year + "-" + month + "-" + date, "YYYY-MM-DD");
        return momentOfBirth.isBefore(working) || momentOfBirth.isSame(working, "day");
    }

    meetsMaxAge(date: Date, month: number, year: number, maxAge: number) {
        const today = new Date();
        const working = today.setFullYear(today.getFullYear() - maxAge);
        const momentOfBirth = moment("" + year + "-" + month + "-" + date, "YYYY-MM-DD");
        return !momentOfBirth.isBefore(working) || momentOfBirth.isSame(working, "day");
    }
}
